import type {
	AgentInlineGuardrail as AgentInlineGuardrailCore,
	AgentSettings as AgentSettingsCore,
	SkillConfig,
	SuggestedPrompt,
} from "@lobu/core";
import { type Query, useQueries, useQuery } from "@tanstack/react-query";
import { ATTENTION_INBOX_QUERY_ROOTS } from "./attention-cache";
import { API_URL, fetchWithTimeout, getPathScope, throwIfNotOk } from "./core";
import { createMutation, createRestMutation } from "./hook-factory";

// ============================================================
// Types
// ============================================================

export interface AgentItem {
	agentId: string;
	name: string;
	description?: string;
	owner: { platform: string; userId: string };
	createdAt: number;
	lastUsedAt?: number;
	connectionCount: number;
	activeConnectionCount: number;
	clientCount: number;
	automationCount: number;
	userCount: number;
	platforms: string[];
	providers: string[];
	status: "active" | "idle";
	/** Workspace-default agent used to own device-executed conversations. */
	system?: boolean;
}

/** Start response, discriminated by `mode`. authorization-code (Claude) returns
 *  a redirect URL to open; device-code (ChatGPT) returns a user code to show. */
type AgentProviderOAuthStartResponse =
	| { mode: "redirect"; authorizeUrl: string }
	| {
			mode: "device";
			userCode: string;
			deviceAuthId: string;
			interval: number;
			verificationUrl: string;
	  };

/** Complete response. Device polls return `pending` until the user authorizes. */
interface AgentProviderOAuthCompleteResponse {
	status: "pending" | "success";
	error?: string;
}

// AgentSkillConfig is the canonical SkillConfig from @lobu/core (the TypeBox
// schema's Static<> in contracts/agent-settings.ts). Re-exported under the
// local name so existing imports keep resolving. The schema is a superset of
// the hand-written shape that used to live here (it also carries
// modelPreference/thinkingLevel); all fields owletto reads are
// present, so this is a safe widening.
export type AgentSkillConfig = SkillConfig;

export interface AgentSkillsCatalogItem {
	skillId: string;
	repo: string;
	name: string;
	description?: string;
	instructions?: string;
	hidden: boolean;
	installed: boolean;
}

interface AgentSkillsCatalogResponse {
	catalog: AgentSkillsCatalogItem[];
	installedSkills: AgentSkillConfig[];
}

// Re-export the canonical core contract so API consumers use the same settings
// shape as the server, including dynamically merged auth profiles.
export type AgentSettings = AgentSettingsCore;

// GuardrailStage is the canonical union from @lobu/core (exported via the
// guardrails module). Imported for local use AND re-exported under the local
// barrel so the ~5 UI files importing it from '@/lib/api' resolve to one
// definition. Core's GuardrailStageSchema is _StageCheck-guarded against
// this same union.
import type { GuardrailStage } from "@lobu/core";

export type { GuardrailStage };

// AgentInlineGuardrail is the canonical shape from @lobu/core (the schema's
// Static<>). Re-exported under the local name so existing imports resolve.
export type AgentInlineGuardrail = AgentInlineGuardrailCore;

export interface GuardrailCatalogItem {
	/** Stable identifier — matches the name registered in `GuardrailRegistry`. */
	name: string;
	/**
	 * Every stage this guardrail is registered at. A name is the unit of
	 * configuration (enabling it arms all of its stages), so a single catalog
	 * item can span more than one stage — e.g. `pii-scan` runs at input,
	 * output, and pre-tool.
	 */
	stages: GuardrailStage[];
	description?: string;
}

export interface GuardrailTripEvent {
	id: number;
	occurredAt: string;
	agentId: string;
	agentName?: string;
	stage: GuardrailStage;
	guardrailName: string;
	reason?: string;
	/** Conversation the trip fired in, when known (egress trips carry it). */
	conversationId?: string;
}

interface CreatedAgentResult {
	agentId: string;
	name: string;
	description?: string;
}

interface UpdatedAgentResult {
	success: true;
}

// ============================================================
// URL helpers
// ============================================================

export function agentsUrl(orgSlug: string, path = "") {
	return `${API_URL}/api/${orgSlug}/agents${path}`;
}

function agentHistoryUrl(agentId: string, path = "") {
	return `${API_URL}/lobu/api/v1/agents/${agentId}/history${path}`;
}

export interface AgentThreadItem {
	id: string;
	title: string;
	/** Trusted connector-owned location label, such as #launch or a DM name. */
	locationLabel?: string | null;
	createdAt: number;
	updatedAt: number;
	/** "web" for app threads; "automation" for automation activity; otherwise the
	 *  source platform (slack, telegram…). */
	platform?: string;
	/** Raw conversation id — used to open a platform conversation read-only. */
	conversationId?: string;
	callCount?: number;
	lastAction?: string | null;
	isRunning?: boolean;
	/** Set on automation entries — routes to the automation's page. */
	automationId?: number;
}

export interface AgentThreadMessage {
	id: string;
	type: string;
	role?: string;
	content: unknown;
	timestamp: string;
	isVerbose?: boolean;
}

/** Durable interaction cards replayed on history load. The transcript doesn't
 * carry these parts, so the server derives approvals from events and the latest
 * terminal agent error from the Postgres-backed response queue. */
export interface ToolApprovalThreadInteraction {
	type: "tool-approval";
	eventId: number;
	runId: number;
	action: string | null;
	proposal: Record<string, unknown> | null;
	current: Record<string, unknown> | null;
	/** entity_field_change proposal: field_path -> proposed value. Present when
	 *  the approval is an agent/automation entity-field write blocked by human
	 *  ownership (distinct from the manage_agents name/description proposal). */
	fields?: Record<string, unknown> | null;
	/** entity_field_change attribution: 'agent' | 'automation'. */
	attribution?: "agent" | "automation" | null;
	/** Discriminator: "agent" | "automation" | "entity". */
	resourceKind?: "agent" | "automation" | "entity" | null;
	/** Durable server explanation for why this proposal requires review. */
	reason?: string | null;
	/** Durable approval lifecycle state. Omitted by legacy history endpoints. */
	status?: string;
	reviewedByName?: string | null;
}

export interface AgentErrorThreadInteraction {
	type: "agent-error";
	runId: number;
	error: string;
	errorCode: string | null;
	errorContext: { provider?: string; model?: string } | null;
}

/** Replayed suggested-action chips: the same `{title, message}` prompts the live
 *  `complete` SSE event carries, so a reload restores the chips shown after the
 *  latest assistant turn. Surfaced as the store's `currentSuggestions` (not a
 *  chat part). */
export interface SuggestionThreadInteraction {
	type: "suggestion";
	prompts: SuggestedPrompt[];
}

export type ThreadInteraction =
	| ToolApprovalThreadInteraction
	| AgentErrorThreadInteraction
	| SuggestionThreadInteraction;

interface ThreadHistory {
	messages: AgentThreadMessage[];
	interactions: ThreadInteraction[];
}

function orgScopedHeaders(orgSlug?: string | null): HeadersInit | undefined {
	return orgSlug ? { "x-lobu-org": orgSlug } : undefined;
}

async function fetchAgentThreads(
	agentId: string,
	orgSlug: string | null | undefined,
	options?: { strict?: boolean; scope?: "all" },
): Promise<AgentThreadItem[]> {
	const path = options?.scope === "all" ? "/threads?scope=all" : "/threads";
	const res = await fetchWithTimeout(agentHistoryUrl(agentId, path), {
		credentials: "include",
		headers: orgScopedHeaders(orgSlug),
	});
	if (res.status === 401 || res.status === 403) return [];
	if (!res.ok) {
		if (options?.strict) {
			await throwIfNotOk(res, "Failed to fetch agent threads");
		}
		return [];
	}
	const data = (await res.json()) as { threads?: AgentThreadItem[] };
	return Array.isArray(data.threads) ? data.threads : [];
}

export function agentThreadsQueryOptions(
	orgSlug: string | null | undefined,
	agentId: string,
	options?: { strict?: boolean; scope?: "all" },
) {
	return {
		queryKey: [
			"agent-threads",
			orgSlug,
			agentId,
			options?.scope ?? "user",
		] as const,
		queryFn: () => fetchAgentThreads(agentId, orgSlug, options),
		staleTime: 15_000,
	};
}

// ============================================================
// Query hooks
// ============================================================

function agentsQueryOptions(slug: string) {
	return {
		queryKey: ["agents", slug] as const,
		queryFn: async (): Promise<AgentItem[]> => {
			const res = await fetchWithTimeout(agentsUrl(slug), {
				credentials: "include",
			});
			if (res.status === 401 || res.status === 403) return [];
			await throwIfNotOk(res, "Failed to fetch agents");
			const data = (await res.json()) as { agents?: AgentItem[] };
			return data.agents ?? [];
		},
		staleTime: 30_000,
	};
}

/**
 * The workspace-default agent: the agent that owns work a device executes
 * (device chats, device-pinned Automations) when nobody picked one.
 */
export function workspaceDefaultAgent(agents: AgentItem[]): AgentItem | null {
	return agents.find((agent) => agent.system) ?? agents[0] ?? null;
}

export function useAgents(options?: { enabled?: boolean }) {
	const { slug } = getPathScope();
	const opts = agentsQueryOptions(slug ?? "");
	return useQuery({
		...opts,
		enabled: !!slug && (options?.enabled ?? true),
	});
}

useAgents.queryOptions = (slug: string) => agentsQueryOptions(slug);

export function useAgentThreads(
	agentId?: string | null,
	options?: { enabled?: boolean; orgSlug?: string | null },
) {
	const { slug } = getPathScope();
	const orgSlug = options?.orgSlug ?? slug;

	return useQuery({
		...agentThreadsQueryOptions(orgSlug, agentId ?? "", { strict: true }),
		enabled: !!orgSlug && !!agentId && (options?.enabled ?? true),
	});
}

/**
 * Recent activity across every agent in a workspace. `scope=all` is the
 * server's consolidated, materialized listing: app/API chats, platform
 * conversations (Slack, Telegram, and any other connector). Automation
 * threads arrive through the same contract but belong in the run stream.
 */
export function useRecentAgentThreads(
	orgSlug?: string | null,
	agentIds?: string[],
	options?: {
		enabled?: boolean;
		limit?: number;
	},
) {
	const limit = options?.limit ?? 8;
	const enabled =
		!!orgSlug && (agentIds?.length ?? 0) > 0 && (options?.enabled ?? true);

	const queries = useQueries({
		queries: (agentIds ?? []).map((agentId) => ({
			...agentThreadsQueryOptions(orgSlug, agentId, {
				strict: true,
				scope: "all",
			}),
			enabled,
			// Poll only while something is running; otherwise window-focus
			// refetch keeps Recent fresh without constant polling.
			refetchInterval: (query: Query<AgentThreadItem[]>) =>
				query.state.data?.some((thread) => thread.isRunning) ? 5_000 : false,
		})),
	});

	const isLoading = enabled && queries.some((query) => query.isLoading);
	const threads = queries
		.flatMap((query, index) => {
			const agentId = agentIds?.[index];
			if (!agentId) return [];
			return (query.data ?? []).map((thread) => ({ agentId, ...thread }));
		})
		// Automation runs have their own Home view; filter before the limit so
		// busy Automations cannot crowd conversations out of recent sessions.
		.filter((thread) => thread.platform !== "automation")
		.sort((a, b) => b.updatedAt - a.updatedAt)
		.slice(0, limit);

	return {
		threads,
		isLoading,
		isError: enabled && queries.some((query) => query.isError),
	};
}

export function useAgentThreadMessages(
	agentId?: string | null,
	threadId?: string | null,
	options?: { enabled?: boolean; orgSlug?: string | null },
) {
	const { slug } = getPathScope();
	const orgSlug = options?.orgSlug ?? slug;

	return useQuery({
		queryKey: ["agent-thread-messages", orgSlug, agentId, threadId],
		queryFn: async (): Promise<ThreadHistory> => {
			const res = await fetchWithTimeout(
				agentHistoryUrl(agentId ?? "", `/threads/${threadId}/messages`),
				{
					credentials: "include",
					headers: orgScopedHeaders(orgSlug),
				},
			);
			if (res.status === 401 || res.status === 403)
				return { messages: [], interactions: [] };
			await throwIfNotOk(res, "Failed to fetch thread messages");
			const data = (await res.json()) as {
				messages?: AgentThreadMessage[];
				interactions?: ThreadInteraction[];
			};
			return {
				messages: Array.isArray(data.messages) ? data.messages : [],
				interactions: Array.isArray(data.interactions) ? data.interactions : [],
			};
		},
		enabled: !!orgSlug && !!agentId && !!threadId && (options?.enabled ?? true),
		staleTime: 5_000,
	});
}

/** A platform conversation's messages (read-only), by raw conversation id. */
export function useConversationMessages(
	agentId?: string | null,
	conversationId?: string | null,
	options?: { enabled?: boolean; orgSlug?: string | null },
) {
	const { slug } = getPathScope();
	const orgSlug = options?.orgSlug ?? slug;
	return useQuery({
		queryKey: ["conversation-messages", orgSlug, agentId, conversationId],
		queryFn: async (): Promise<AgentThreadMessage[]> => {
			const res = await fetchWithTimeout(
				agentHistoryUrl(
					agentId ?? "",
					`/conversations/${encodeURIComponent(conversationId ?? "")}/messages`,
				),
				{ credentials: "include", headers: orgScopedHeaders(orgSlug) },
			);
			if (res.status === 401 || res.status === 403) return [];
			await throwIfNotOk(res, "Failed to fetch conversation");
			const data = (await res.json()) as { messages?: AgentThreadMessage[] };
			return Array.isArray(data.messages) ? data.messages : [];
		},
		enabled:
			!!orgSlug && !!agentId && !!conversationId && (options?.enabled ?? true),
		staleTime: 5_000,
	});
}

interface AutomationRunThreadItem {
	runId: number;
	completedAt: string;
	status: string;
	task: string | null;
	pendingActionCount: number;
	autoAppliedCount: number;
	messages: AgentThreadMessage[];
	actions: ToolApprovalThreadInteraction[];
}

interface AutomationRunThreadHistory {
	runs: AutomationRunThreadItem[];
}

/** An automation's recent completed runs, newest-first, as raw transcripts. The
 *  caller stitches them into one read-only thread. */
export function useAutomationRunThreads(
	agentId?: string | null,
	automationId?: string | number | null,
	options?: {
		enabled?: boolean;
		orgSlug?: string | null;
		limit?: number;
		/** Fetch one specific run regardless of the recency limit. */
		runId?: number;
	},
) {
	const { slug } = getPathScope();
	const orgSlug = options?.orgSlug ?? slug;
	const limit = options?.limit ?? 20;
	const runId = options?.runId;

	return useQuery({
		queryKey: [
			"automation-run-threads",
			orgSlug,
			agentId,
			automationId,
			limit,
			runId ?? null,
		],
		queryFn: async (): Promise<AutomationRunThreadHistory> => {
			const res = await fetchWithTimeout(
				agentHistoryUrl(
					agentId ?? "",
					`/automations/${automationId}/thread?limit=${limit}${runId != null ? `&run_id=${runId}` : ""}`,
				),
				{ credentials: "include", headers: orgScopedHeaders(orgSlug) },
			);
			if (res.status === 401 || res.status === 403) {
				return { runs: [] };
			}
			await throwIfNotOk(res, "Failed to fetch Automation runs");
			const data = (await res.json()) as Partial<AutomationRunThreadHistory>;
			return {
				runs: Array.isArray(data.runs) ? data.runs : [],
			};
		},
		enabled:
			!!orgSlug && !!agentId && !!automationId && (options?.enabled ?? true),
		staleTime: 5_000,
	});
}

export function useAgentConfig(
	agentId?: string | null,
	options?: { enabled?: boolean },
) {
	const { slug } = getPathScope();

	return useQuery({
		queryKey: ["agent-config", slug, agentId],
		queryFn: async (): Promise<AgentSettings> => {
			const res = await fetchWithTimeout(
				agentsUrl(slug!, `/${agentId}/config`),
				{
					credentials: "include",
				},
			);
			await throwIfNotOk(res, "Failed to fetch agent config");
			return res.json() as Promise<AgentSettings>;
		},
		enabled: !!slug && !!agentId && (options?.enabled ?? true),
		staleTime: 30_000,
	});
}

// ----------------------------------------------------------------
// Guardrails
// ----------------------------------------------------------------

/**
 * Lists guardrails registered with the gateway for an agent. Resolves to
 * `null` when the registry is unavailable — the UI renders a coming-soon
 * state instead of treating it as an empty registry.
 */
export function useGuardrailsCatalog(
	agentId?: string | null,
	options?: { enabled?: boolean },
) {
	const { slug } = getPathScope();
	return useQuery<GuardrailCatalogItem[] | null>({
		queryKey: ["guardrails-catalog", slug, agentId],
		queryFn: async () => {
			const res = await fetchWithTimeout(
				`${API_URL}/api/${slug}/installed/agents/${agentId}/installed?kinds=guardrails`,
				{ credentials: "include" },
			);
			if (res.status === 404) return null;
			await throwIfNotOk(res, "Failed to fetch guardrails");
			const data = (await res.json()) as {
				installed?: {
					guardrails?: {
						items?: Array<{
							id: string;
							name: string;
							detail?: Record<string, unknown>;
						}>;
					};
				};
			};
			const items = data.installed?.guardrails?.items;
			if (!items) return null;
			return items.map((item) => ({
				name: item.name,
				stages: (item.detail?.stages as GuardrailStage[] | undefined) ?? [],
			}));
		},
		enabled: !!slug && !!agentId && (options?.enabled ?? true),
		staleTime: 60_000,
	});
}

/**
 * Recent `guardrail-trip` events for the agent. Pass `guardrail` to narrow to a
 * single guardrail's catches (the per-guardrail detail view).
 */
export function useGuardrailTrips(
	agentId?: string | null,
	options?: { guardrail?: string; conversationId?: string },
) {
	const { slug } = getPathScope();
	const guardrail = options?.guardrail;
	const conversationId = options?.conversationId;
	return useQuery({
		queryKey: [
			"guardrail-trips",
			slug,
			agentId,
			guardrail ?? null,
			conversationId ?? null,
		],
		queryFn: async (): Promise<GuardrailTripEvent[]> => {
			const params = new URLSearchParams({ limit: "50" });
			if (guardrail) params.set("guardrail", guardrail);
			if (conversationId) params.set("conversationId", conversationId);
			const res = await fetchWithTimeout(
				agentsUrl(slug!, `/${agentId}/guardrail-trips?${params.toString()}`),
				{ credentials: "include" },
			);
			// 404 = endpoint not yet deployed; render the empty state. Any
			// other non-2xx surfaces via React Query's isError.
			if (res.status === 404) return [];
			await throwIfNotOk(res, "Failed to fetch guardrail trips");
			const data = (await res.json()) as { trips?: GuardrailTripEvent[] };
			return data.trips ?? [];
		},
		enabled: !!slug && !!agentId,
		staleTime: 15_000,
		retry: false,
	});
}

/**
 * The gateway's default judge model for custom guardrails (from
 * `EGRESS_JUDGE_MODEL`). `null` when unset — in that case the create/edit UI
 * requires a per-guardrail model. There is no hardcoded fallback.
 */
export function useGuardrailJudgeDefault(agentId?: string | null) {
	const { slug } = getPathScope();
	return useQuery({
		queryKey: ["guardrail-judge-default", slug, agentId],
		queryFn: async (): Promise<string | null> => {
			const res = await fetchWithTimeout(
				agentsUrl(slug!, `/${agentId}/guardrail-judge-default`),
				{
					credentials: "include",
				},
			);
			if (res.status === 404) return null;
			await throwIfNotOk(res, "Failed to fetch judge model default");
			const data = (await res.json()) as { defaultModel?: string | null };
			return data.defaultModel ?? null;
		},
		enabled: !!slug && !!agentId,
		staleTime: 60_000,
	});
}

// ----------------------------------------------------------------

export function useAgentSkillsCatalog(
	agentId?: string | null,
	options?: { enabled?: boolean },
) {
	const { slug } = getPathScope();

	return useQuery({
		queryKey: ["agent-skills-catalog", slug, agentId],
		queryFn: async (): Promise<AgentSkillsCatalogResponse> => {
			if (!slug || !agentId) {
				return { catalog: [], installedSkills: [] };
			}

			const res = await fetchWithTimeout(
				`${API_URL}/api/${slug}/installed/agents/${agentId}/installed?kinds=skills&include=catalog`,
				{ credentials: "include" },
			);
			await throwIfNotOk(res, "Failed to fetch skills catalog");
			const data = (await res.json()) as {
				installed?: {
					skills?: {
						items?: Array<{
							id: string;
							name: string;
							detail?: Record<string, unknown>;
						}>;
					};
				};
			};
			const items = data.installed?.skills?.items ?? [];

			const installedSkills = items
				.filter((item) => item.detail?.enabled !== undefined)
				.map((item) => ({
					repo: item.id,
					name: item.name,
					enabled: Boolean(item.detail?.enabled),
					description:
						typeof item.detail?.description === "string"
							? item.detail.description
							: undefined,
					system: Boolean(item.detail?.system),
				}));

			const catalog = items
				.filter((item) => !item.detail?.hidden)
				.map((item) => ({
					skillId: item.id.replace(/^bundled\//, ""),
					repo: item.id,
					name: item.name,
					description:
						typeof item.detail?.description === "string"
							? item.detail.description
							: undefined,
					instructions:
						typeof item.detail?.instructions === "string"
							? item.detail.instructions
							: undefined,
					hidden: false,
					installed: Boolean(item.detail?.installed),
				}));

			return { catalog, installedSkills };
		},
		enabled: !!slug && (options?.enabled ?? true),
		staleTime: 0,
		refetchOnMount: "always",
	});
}

// ── LLM-provider OAuth (subscription login) ──────────────────────────────────
//
// ONE generic org-scoped pair backs both flows. The token lands in the per-user
// ORG BUCKET on the server, so a sign-in here covers all the user's agents in
// this org — `agentId` is no longer part of the request. Claude runs
// authorization-code (redirect + paste `code#state`); ChatGPT runs device-code
// (show a user code + poll). The start response is discriminated by `mode`.

/** POST /inference-providers/oauth/start — begin a provider sign-in. */
export async function startProviderOAuth(
	orgSlug: string,
	providerId: string,
): Promise<AgentProviderOAuthStartResponse> {
	const res = await fetchWithTimeout(
		agentsUrl(orgSlug, `/inference-providers/oauth/start`),
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ providerId }),
		},
	);
	await throwIfNotOk(res, "Failed to start sign-in");
	return res.json() as Promise<AgentProviderOAuthStartResponse>;
}

/**
 * POST /inference-providers/oauth/complete — finish a provider sign-in.
 * Pass `{ code }` (authorization-code, `code#state`) OR
 * `{ deviceAuthId, userCode }` (device-code poll). Device polls return
 * `{ status: "pending" }` until the user authorizes.
 */
export async function completeProviderOAuth(
	orgSlug: string,
	providerId: string,
	input: { code: string } | { deviceAuthId: string; userCode: string },
): Promise<AgentProviderOAuthCompleteResponse> {
	const res = await fetchWithTimeout(
		agentsUrl(orgSlug, `/inference-providers/oauth/complete`),
		{
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ providerId, ...input }),
		},
	);
	await throwIfNotOk(res, "Failed to complete sign-in");
	return res.json() as Promise<AgentProviderOAuthCompleteResponse>;
}

// ============================================================
// Mutation hooks
// ============================================================

export const useCreateAgent = createRestMutation<
	[],
	{ agentId: string; name: string; description?: string },
	CreatedAgentResult
>({
	method: "POST",
	url: ({ slug }) => agentsUrl(slug),
	body: (params) => params,
	invalidate: () => [["agents"]],
	successMessage: "Agent created",
	errorMessage: "Failed to create agent",
});

export const useUpdateAgent = createRestMutation<
	[agentId: string],
	{ name?: string; description?: string },
	UpdatedAgentResult
>({
	method: "PATCH",
	url: ({ slug, args: [agentId] }) => agentsUrl(slug, `/${agentId}`),
	body: (params) => params,
	invalidate: ({ slug, args: [agentId] }) => [
		["agents"],
		["agent", slug, agentId],
	],
	errorMessage: "Failed to update agent",
});

export const useDeleteAgent = createRestMutation<[agentId: string]>({
	method: "DELETE",
	url: ({ slug, args: [agentId] }) => agentsUrl(slug, `/${agentId}`),
	read: "none",
	invalidate: () => [["agents"]],
	successMessage: "Agent deleted",
	errorMessage: "Failed to delete agent",
});

export const useUpdateAgentConfig = createRestMutation<
	[agentId: string],
	Partial<AgentSettings>,
	AgentSettings
>({
	method: "PATCH",
	url: ({ slug, args: [agentId] }) => agentsUrl(slug, `/${agentId}/config`),
	body: (params) => params,
	invalidate: ({ slug, args: [agentId] }) => [
		["agent-config", slug, agentId],
		["agent", slug, agentId],
	],
	errorMessage: "Failed to update agent config",
});

// ============================================================
// Builder gate — approve / reject a held manage_agents write
// ============================================================
//
// The per-org builder agent's create/update/delete calls are server-gated:
// they produce a pending `runs` row + an approval card instead of mutating
// immediately. Approve/reject ride the SAME durable runs/events primitive that
// connector operations use, so they go through `manage_operations` (whose
// approve/reject handlers branch on the manage_agents run). The builder chat
// card (agent-thread `InteractiveToolPart`, via the interaction MCP App bridge)
// calls these.

export const useApproveAgentChange = createMutation<
	{ run_id: number },
	{ approved: true; run_id: number; event_id?: number; message: string }
>({
	tool: "manage_operations",
	body: (p) => ({ action: "approve", run_id: p.run_id }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		"agents",
		"automation-run-threads",
		// Refetch the config-prefill review query so an approved run resolves to
		// its 404 "no longer pending" state (re-enables the form) instead of
		// leaving a live Approve card on a run that's already applied.
		"pending-proposal",
	],
	successMessage: "Change approved — applying",
	invalidateOnError: true,
});

export const useRejectAgentChange = createMutation<
	{ run_id: number; reason?: string },
	{ rejected: true; run_id: number; event_id?: number }
>({
	tool: "manage_operations",
	body: (p) => ({
		action: "reject",
		run_id: p.run_id,
		...(p.reason ? { reason: p.reason } : {}),
	}),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		"agents",
		"automation-run-threads",
		"pending-proposal",
	],
	successMessage: "Change rejected",
	invalidateOnError: true,
});

export const useApproveAgentChangeBatch = createMutation<
	{ run_id: number; run_ids: number[] },
	{
		approved_count: number;
		failed_count: number;
		run_id: number;
		run_ids: number[];
		message: string;
	}
>({
	tool: "manage_operations",
	body: (p) => ({ action: "approve_batch", ...p }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		"agents",
		"automation-run-threads",
	],
	transform: (result) => {
		if (result.failed_count > 0 || result.run_ids.length === 0) {
			throw new Error(result.message || "Run approvals could not be applied");
		}
		return result;
	},
	successMessage: "Run approvals applied",
	invalidateOnError: true,
});

export const useRejectAgentChangeBatch = createMutation<
	{ run_id: number; run_ids: number[] },
	{
		rejected_count: number;
		run_id: number;
		run_ids: number[];
		message: string;
	}
>({
	tool: "manage_operations",
	body: (p) => ({ action: "reject_batch", ...p }),
	invalidateKeys: [
		...ATTENTION_INBOX_QUERY_ROOTS,
		"agents",
		"automation-run-threads",
	],
	transform: (result) => {
		if (
			result.run_ids.length === 0 ||
			result.rejected_count !== result.run_ids.length
		) {
			throw new Error(result.message || "Run approvals could not be rejected");
		}
		return result;
	},
	successMessage: "Run approvals rejected",
	invalidateOnError: true,
});
